/**
 * Candlestick + volume renderer.
 *
 * This is the hottest draw path in the terminal, so the structure is dictated
 * by canvas cost rather than by what reads most naturally:
 *
 *   - Candles are batched into four passes (up wicks, down wicks, up bodies,
 *     down bodies) instead of drawn one at a time. Setting `fillStyle` between
 *     every candle costs a state change each; on a 900-bar chart that is 1800
 *     state changes per frame versus four.
 *   - All x/y coordinates are rounded before use. A body at x=143.27 makes the
 *     rasteriser antialias two columns of pixels, which both looks soft and
 *     doubles the fill cost.
 *   - Text is drawn last, in one pass per style, because switching `font` also
 *     invalidates the text measurement cache.
 *
 * The renderer holds no state about the data — it is handed a viewport and a
 * series each frame and draws exactly what those describe.
 */

import { crisp } from '../lib/frame';
import { compact, dayMonth, hm, hms, price as fmtPrice } from '../lib/num';
import { C, F } from '../styles/palette';
import { serverNow } from '../data/binance';
import type { KlineSeries } from '../data/klines';
import type { ChartViewport } from './scales';

export interface HoverState {
  active: boolean;
  x: number;
  y: number;
  /** Bar index under the pointer, clamped to the series. -1 when off-data. */
  index: number;
}

export interface ChartTheme {
  showGrid: boolean;
  showVolume: boolean;
  candleStyle: 'candles' | 'bars' | 'line' | 'area';
}

export const DEFAULT_THEME: ChartTheme = {
  showGrid: true,
  showVolume: true,
  candleStyle: 'candles',
};

export class ChartRenderer {
  private priceTickBuf: number[] = [];
  private timeTickBuf: number[] = [];

  // -- background & grid ----------------------------------------------------

  clear(ctx: CanvasRenderingContext2D, w: number, h: number): void {
    ctx.fillStyle = C.bg1;
    ctx.fillRect(0, 0, w, h);
  }

  drawGrid(ctx: CanvasRenderingContext2D, vp: ChartViewport, series: KlineSeries): void {
    const ticks = vp.priceTicks(56, this.priceTickBuf);
    const left = vp.plotLeft;
    const right = vp.plotRight;

    ctx.strokeStyle = C.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let i = 0; i < ticks.length; i++) {
      const y = crisp(vp.yOf(ticks[i]));
      if (y < vp.priceTop - 1 || y > vp.priceBottom + 1) continue;
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
    }

    const tt = vp.timeTicks(series, 92, this.timeTickBuf);
    for (let i = 0; i < tt.length; i++) {
      const x = crisp(vp.xOf(tt[i]));
      if (x < left || x > right) continue;
      ctx.moveTo(x, vp.bodyTop);
      ctx.lineTo(x, vp.bodyBottom);
    }
    ctx.stroke();

    // Pane separators sit above the grid so they read as structure.
    ctx.strokeStyle = C.line;
    ctx.beginPath();
    if (vp.volumeHeight > 0) {
      const y = crisp(vp.volumeTop);
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
    }
    if (vp.subPaneHeight > 0) {
      const y = crisp(vp.subPaneTop);
      ctx.moveTo(left, y);
      ctx.lineTo(right, y);
    }
    ctx.stroke();
  }

  // -- volume ---------------------------------------------------------------

  drawVolume(
    ctx: CanvasRenderingContext2D,
    vp: ChartViewport,
    series: KlineSeries,
    hover: HoverState,
  ): void {
    const h = vp.volumeHeight;
    if (h <= 4 || series.length === 0) return;

    const maxVol = vp.visibleMaxVolume(series);
    const lo = Math.max(0, vp.firstVisible);
    const hi = Math.min(series.length - 1, vp.lastVisible);
    if (hi < lo) return;

    const bw = Math.max(1, Math.floor(vp.barWidth * 0.72));
    const half = bw / 2;
    const bottom = vp.volumeBottom;
    const open = series.open.data;
    const close = series.close.data;
    const vol = series.volume.data;

    // Bars are positioned from bar centres and can extend past the plot edge
    // when panned; without a clip they paint over the price axis gutter.
    ctx.save();
    ctx.beginPath();
    ctx.rect(vp.plotLeft, vp.volumeTop, vp.plotWidth, vp.volumeHeight);
    ctx.clip();

    // Two passes, one fillStyle change each. The bodies are solid enough to
    // read as a histogram in their own right — an earlier version used the
    // same ~10% wash as the chart background tints, which left the pane
    // looking empty at a glance.
    for (let pass = 0; pass < 2; pass++) {
      ctx.fillStyle = pass === 0 ? 'rgba(46,189,133,0.42)' : 'rgba(246,70,93,0.42)';
      ctx.beginPath();
      for (let i = lo; i <= hi; i++) {
        const isUp = close[i] >= open[i];
        if ((pass === 0) !== isUp) continue;
        const v = vol[i];
        if (!(v > 0)) continue;
        const x = Math.round(vp.xOf(i) - half);
        const top = vp.yOfVolume(v, maxVol);
        const barH = Math.max(1, bottom - top);
        ctx.rect(x, Math.round(bottom - barH), bw, barH);
      }
      ctx.fill();
    }

    // A brighter cap keeps very short bars visible against the pane.
    for (let pass = 0; pass < 2; pass++) {
      ctx.fillStyle = pass === 0 ? C.upBright : C.downBright;
      ctx.globalAlpha = 0.85;
      ctx.beginPath();
      for (let i = lo; i <= hi; i++) {
        const isUp = close[i] >= open[i];
        if ((pass === 0) !== isUp) continue;
        const v = vol[i];
        if (!(v > 0)) continue;
        const x = Math.round(vp.xOf(i) - half);
        const top = Math.round(vp.yOfVolume(v, maxVol));
        ctx.rect(x, top, bw, Math.min(2, Math.max(1, bottom - top)));
      }
      ctx.fill();
      ctx.globalAlpha = 1;
    }

    ctx.restore();

    // Readout for the bar under the crosshair, falling back to the newest bar.
    // This is the number the pane exists to communicate; labelling only the
    // pane maximum meant hovering a bar told you nothing about that bar.
    const idx = hover.active && hover.index >= 0 && hover.index < series.length
      ? hover.index
      : series.length - 1;
    const v = series.volume.get(idx);
    const isUp = series.close.get(idx) >= series.open.get(idx);

    ctx.font = F.micro;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';
    ctx.fillStyle = C.text3;
    ctx.fillText('VOL', vp.plotLeft + 6, vp.volumeTop + 3);
    ctx.fillStyle = isUp ? C.up : C.down;
    ctx.fillText(compact(v), vp.plotLeft + 6 + ctx.measureText('VOL').width + 5, vp.volumeTop + 3);

    // Pane maximum stays available on the axis so the scale is still readable.
    ctx.fillStyle = C.text3;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(compact(maxVol), vp.plotRight + 6, vp.volumeTop + 8);

    // Crosshair value tag: what volume does the pointer's height correspond to?
    if (hover.active && hover.y > vp.volumeTop && hover.y < vp.volumeBottom) {
      const frac = (vp.volumeBottom - hover.y) / (h * 0.88);
      const atPointer = Math.max(0, frac * maxVol);
      this.axisTag(ctx, vp, hover.y, compact(atPointer), C.bg3, C.text0);
    }
  }

  /** Shared right-axis tag used by the crosshair in every pane. */
  axisTag(
    ctx: CanvasRenderingContext2D,
    vp: ChartViewport,
    y: number,
    text: string,
    bg: string,
    fg: string,
  ): void {
    const tagH = 14;
    const tagY = Math.round(y - tagH / 2);
    ctx.fillStyle = bg;
    ctx.fillRect(vp.plotRight + 1, tagY, vp.insets.right - 1, tagH);
    ctx.font = F.micro;
    ctx.fillStyle = fg;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, vp.plotRight + 6, tagY + tagH / 2 + 0.5);
  }

  // -- price series ---------------------------------------------------------

  drawSeries(
    ctx: CanvasRenderingContext2D,
    vp: ChartViewport,
    series: KlineSeries,
    theme: ChartTheme,
  ): void {
    if (series.length === 0) return;
    switch (theme.candleStyle) {
      case 'line':
        this.drawLine(ctx, vp, series, false);
        break;
      case 'area':
        this.drawLine(ctx, vp, series, true);
        break;
      case 'bars':
        this.drawBars(ctx, vp, series);
        break;
      default:
        this.drawCandles(ctx, vp, series);
    }
  }

  private drawCandles(
    ctx: CanvasRenderingContext2D,
    vp: ChartViewport,
    series: KlineSeries,
  ): void {
    const lo = Math.max(0, vp.firstVisible);
    const hi = Math.min(series.length - 1, vp.lastVisible);
    if (hi < lo) return;

    const open = series.open.data;
    const high = series.high.data;
    const low = series.low.data;
    const close = series.close.data;

    const bw = vp.candleWidth;
    const half = Math.floor(bw / 2);
    const thin = vp.barWidth < 3;

    ctx.save();
    // Clip so a candle at the edge cannot bleed into the price axis.
    ctx.beginPath();
    ctx.rect(vp.plotLeft, vp.priceTop, vp.plotWidth, vp.priceHeight);
    ctx.clip();

    if (thin) {
      // Below ~3px per bar a body is meaningless. Draw the high-low range as
      // a 1px column, coloured by direction. This is what keeps a 2000-bar
      // zoom-out legible instead of a solid smear.
      for (let pass = 0; pass < 2; pass++) {
        ctx.strokeStyle = pass === 0 ? C.up : C.down;
        ctx.lineWidth = 1;
        ctx.beginPath();
        for (let i = lo; i <= hi; i++) {
          const isUp = close[i] >= open[i];
          if ((pass === 0) !== isUp) continue;
          const x = crisp(vp.xOf(i));
          ctx.moveTo(x, Math.round(vp.yOf(high[i])));
          ctx.lineTo(x, Math.round(vp.yOf(low[i])));
        }
        ctx.stroke();
      }
      ctx.restore();
      return;
    }

    // Wicks first so bodies sit on top of them.
    for (let pass = 0; pass < 2; pass++) {
      ctx.strokeStyle = pass === 0 ? C.upWick : C.downWick;
      ctx.lineWidth = 1;
      ctx.beginPath();
      for (let i = lo; i <= hi; i++) {
        const isUp = close[i] >= open[i];
        if ((pass === 0) !== isUp) continue;
        const x = crisp(vp.xOf(i));
        ctx.moveTo(x, Math.round(vp.yOf(high[i])));
        ctx.lineTo(x, Math.round(vp.yOf(low[i])));
      }
      ctx.stroke();
    }

    for (let pass = 0; pass < 2; pass++) {
      ctx.fillStyle = pass === 0 ? C.up : C.down;
      ctx.beginPath();
      for (let i = lo; i <= hi; i++) {
        const isUp = close[i] >= open[i];
        if ((pass === 0) !== isUp) continue;
        const yO = vp.yOf(open[i]);
        const yC = vp.yOf(close[i]);
        const top = Math.round(Math.min(yO, yC));
        // A doji has zero body height; force 1px or it disappears entirely.
        const bodyH = Math.max(1, Math.round(Math.abs(yC - yO)));
        ctx.rect(Math.round(vp.xOf(i)) - half, top, bw, bodyH);
      }
      ctx.fill();
    }

    ctx.restore();
  }

  private drawBars(ctx: CanvasRenderingContext2D, vp: ChartViewport, series: KlineSeries): void {
    const lo = Math.max(0, vp.firstVisible);
    const hi = Math.min(series.length - 1, vp.lastVisible);
    if (hi < lo) return;
    const open = series.open.data;
    const high = series.high.data;
    const low = series.low.data;
    const close = series.close.data;
    const tick = Math.max(1, Math.floor(vp.barWidth * 0.34));

    ctx.save();
    ctx.beginPath();
    ctx.rect(vp.plotLeft, vp.priceTop, vp.plotWidth, vp.priceHeight);
    ctx.clip();

    for (let pass = 0; pass < 2; pass++) {
      ctx.strokeStyle = pass === 0 ? C.up : C.down;
      ctx.lineWidth = vp.barWidth > 6 ? 1.5 : 1;
      ctx.beginPath();
      for (let i = lo; i <= hi; i++) {
        const isUp = close[i] >= open[i];
        if ((pass === 0) !== isUp) continue;
        const x = crisp(vp.xOf(i));
        ctx.moveTo(x, Math.round(vp.yOf(high[i])));
        ctx.lineTo(x, Math.round(vp.yOf(low[i])));
        const yO = crisp(vp.yOf(open[i]));
        ctx.moveTo(x - tick, yO);
        ctx.lineTo(x, yO);
        const yC = crisp(vp.yOf(close[i]));
        ctx.moveTo(x, yC);
        ctx.lineTo(x + tick, yC);
      }
      ctx.stroke();
    }
    ctx.restore();
  }

  private drawLine(
    ctx: CanvasRenderingContext2D,
    vp: ChartViewport,
    series: KlineSeries,
    area: boolean,
  ): void {
    const lo = Math.max(0, vp.firstVisible);
    const hi = Math.min(series.length - 1, vp.lastVisible);
    if (hi <= lo) return;
    const close = series.close.data;

    ctx.save();
    ctx.beginPath();
    ctx.rect(vp.plotLeft, vp.priceTop, vp.plotWidth, vp.priceHeight);
    ctx.clip();

    ctx.beginPath();
    for (let i = lo; i <= hi; i++) {
      const x = vp.xOf(i);
      const y = vp.yOf(close[i]);
      if (i === lo) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }

    if (area) {
      const grad = ctx.createLinearGradient(0, vp.priceTop, 0, vp.priceBottom);
      const rising = close[hi] >= close[lo];
      grad.addColorStop(0, rising ? 'rgba(46,189,133,0.28)' : 'rgba(246,70,93,0.28)');
      grad.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.save();
      ctx.lineTo(vp.xOf(hi), vp.priceBottom);
      ctx.lineTo(vp.xOf(lo), vp.priceBottom);
      ctx.closePath();
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.restore();

      ctx.beginPath();
      for (let i = lo; i <= hi; i++) {
        const x = vp.xOf(i);
        const y = vp.yOf(close[i]);
        if (i === lo) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
    }

    ctx.strokeStyle = close[hi] >= close[lo] ? C.upBright : C.downBright;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = 'round';
    ctx.stroke();
    ctx.restore();
  }

  // -- axes -----------------------------------------------------------------

  drawPriceAxis(
    ctx: CanvasRenderingContext2D,
    vp: ChartViewport,
    precision: number,
  ): void {
    const x = vp.plotRight;
    ctx.fillStyle = C.bg1;
    ctx.fillRect(x, 0, vp.insets.right, vp.height);

    ctx.strokeStyle = C.line;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(crisp(x), 0);
    ctx.lineTo(crisp(x), vp.bodyBottom);
    ctx.stroke();

    const decimals = vp.priceTickDecimals(precision);
    const ticks = vp.priceTicks(56, this.priceTickBuf);
    ctx.font = F.xs;
    ctx.fillStyle = C.text2;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    for (let i = 0; i < ticks.length; i++) {
      const y = vp.yOf(ticks[i]);
      if (y < vp.priceTop + 6 || y > vp.priceBottom - 4) continue;
      ctx.fillText(fmtPrice(ticks[i], decimals), x + 6, y);
    }
  }

  drawTimeAxis(ctx: CanvasRenderingContext2D, vp: ChartViewport, series: KlineSeries): void {
    const y = vp.bodyBottom;
    ctx.fillStyle = C.bg1;
    ctx.fillRect(0, y, vp.width, vp.insets.bottom);
    ctx.strokeStyle = C.line;
    ctx.beginPath();
    ctx.moveTo(0, crisp(y));
    ctx.lineTo(vp.width, crisp(y));
    ctx.stroke();

    if (series.length === 0) return;
    const ticks = vp.timeTicks(series, 92, this.timeTickBuf);
    const intraday = series.intervalMs < 86_400_000;

    ctx.font = F.xs;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const midY = y + vp.insets.bottom / 2;
    let prevDay = -1;

    for (let i = 0; i < ticks.length; i++) {
      const idx = ticks[i];
      const px = vp.xOf(idx);
      if (px < vp.plotLeft + 20 || px > vp.plotRight - 20) continue;
      const t = series.openTime.get(Math.min(series.length - 1, Math.max(0, idx)));
      const d = new Date(t);
      const day = d.getDate();
      // Label the first tick of each day with the date instead of the time —
      // otherwise a multi-day intraday chart is a wall of identical "00:00".
      const isNewDay = day !== prevDay;
      prevDay = day;
      if (intraday && !isNewDay) {
        ctx.fillStyle = C.text2;
        ctx.fillText(hm(t), px, midY);
      } else {
        ctx.fillStyle = C.text1;
        ctx.fillText(dayMonth(t), px, midY);
      }
    }
  }

  // -- last price -----------------------------------------------------------

  /**
   * The last-price line, its axis tag, and the countdown to the current bar's
   * close. The countdown is the thing that tells you at a glance whether the
   * rightmost candle is settled or still moving.
   */
  drawLastPrice(
    ctx: CanvasRenderingContext2D,
    vp: ChartViewport,
    series: KlineSeries,
    precision: number,
  ): void {
    const n = series.length;
    if (n === 0) return;
    const last = series.close.get(n - 1);
    const open = series.open.get(n - 1);
    const up = last >= open;
    const y = vp.yOf(last);
    if (y < vp.priceTop - 20 || y > vp.priceBottom + 20) return;

    const col = up ? C.up : C.down;

    ctx.save();
    ctx.setLineDash([3, 3]);
    ctx.strokeStyle = col;
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(vp.plotLeft, crisp(y));
    ctx.lineTo(vp.plotRight, crisp(y));
    ctx.stroke();
    ctx.restore();

    // Axis tag
    const label = fmtPrice(last, precision);
    const tagH = 15;
    const tagY = Math.round(Math.max(vp.priceTop, Math.min(vp.priceBottom - tagH, y - tagH / 2)));
    ctx.fillStyle = col;
    ctx.fillRect(vp.plotRight + 1, tagY, vp.insets.right - 1, tagH);
    ctx.font = F.xsBold;
    ctx.fillStyle = '#04070a';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, vp.plotRight + 6, tagY + tagH / 2 + 0.5);

    // Bar-close countdown under the tag. Measured against the exchange clock:
    // with a skewed local clock `remain` exceeds the interval and the guard
    // below silently suppresses the countdown entirely.
    const barEnd = series.openTime.get(n - 1) + series.intervalMs;
    const remain = barEnd - serverNow();
    if (remain > 0 && remain < series.intervalMs * 1.5) {
      const secs = Math.floor(remain / 1000);
      const mm = Math.floor(secs / 60);
      const ss = secs % 60;
      const txt =
        mm >= 60
          ? `${Math.floor(mm / 60)}h${String(mm % 60).padStart(2, '0')}`
          : `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
      const cy = tagY + tagH + 1;
      if (cy + 13 < vp.bodyBottom) {
        ctx.fillStyle = C.bg3;
        ctx.fillRect(vp.plotRight + 1, cy, vp.insets.right - 1, 13);
        ctx.font = F.micro;
        ctx.fillStyle = remain < 10_000 ? C.accent : C.text2;
        ctx.fillText(txt, vp.plotRight + 6, cy + 7);
      }
    }
  }

  /**
   * Best bid / best ask, as short ticks against the price axis.
   *
   * Deliberately not labelled. An earlier version printed the price next to
   * each tick, which put two extra numbers floating in the middle of the plot
   * within a few pixels of the last-price tag that already says the same
   * thing — three overlapping readouts for one piece of information.
   */
  drawQuotes(ctx: CanvasRenderingContext2D, vp: ChartViewport, bid: number, ask: number): void {
    const draw = (v: number, color: string) => {
      if (!Number.isFinite(v)) return;
      const y = vp.yOf(v);
      if (y < vp.priceTop || y > vp.priceBottom) return;
      ctx.strokeStyle = color;
      ctx.globalAlpha = 0.5;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(vp.plotRight - 9, crisp(y));
      ctx.lineTo(vp.plotRight, crisp(y));
      ctx.stroke();
      ctx.globalAlpha = 1;
    };
    draw(bid, C.up);
    draw(ask, C.down);
  }

  // -- crosshair ------------------------------------------------------------

  drawCrosshair(
    ctx: CanvasRenderingContext2D,
    vp: ChartViewport,
    series: KlineSeries,
    hover: HoverState,
    precision: number,
  ): void {
    if (!hover.active) return;
    const x = hover.x;
    const y = hover.y;
    if (x < vp.plotLeft || x > vp.plotRight) return;

    // Snap the vertical line to the centre of the bar under the pointer.
    const snapX = hover.index >= 0 ? crisp(vp.xOf(hover.index)) : crisp(x);

    ctx.save();
    ctx.setLineDash([2, 3]);
    ctx.strokeStyle = C.crosshair;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(snapX, vp.bodyTop);
    ctx.lineTo(snapX, vp.bodyBottom);
    if (y >= vp.bodyTop && y <= vp.bodyBottom) {
      ctx.moveTo(vp.plotLeft, crisp(y));
      ctx.lineTo(vp.plotRight, crisp(y));
    }
    ctx.stroke();
    ctx.restore();

    // Price tag on the right axis
    if (y >= vp.priceTop && y <= vp.priceBottom) {
      const p = vp.priceAt(y);
      const label = fmtPrice(p, vp.priceTickDecimals(precision));
      const tagH = 15;
      const tagY = Math.round(y - tagH / 2);
      ctx.fillStyle = C.crosshairLabel;
      ctx.fillRect(vp.plotRight + 1, tagY, vp.insets.right - 1, tagH);
      ctx.font = F.xs;
      ctx.fillStyle = C.text0;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(label, vp.plotRight + 6, tagY + tagH / 2 + 0.5);
    }

    // Time tag on the bottom axis
    if (series.length > 0) {
      const t = series.timeAtIndex(hover.index >= 0 ? hover.index : vp.indexAt(x));
      const txt = series.intervalMs >= 86_400_000 ? dayMonth(t) : `${dayMonth(t)} ${hm(t)}`;
      ctx.font = F.xs;
      const w = ctx.measureText(txt).width + 12;
      const tx = Math.max(0, Math.min(vp.width - w, snapX - w / 2));
      ctx.fillStyle = C.crosshairLabel;
      ctx.fillRect(tx, vp.bodyBottom + 1, w, vp.insets.bottom - 2);
      ctx.fillStyle = C.text0;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(txt, tx + w / 2, vp.bodyBottom + vp.insets.bottom / 2);
    }
  }

  /**
   * OHLC readout for the hovered bar (or the latest when not hovering),
   * drawn top-left over the plot.
   */
  drawLegend(
    ctx: CanvasRenderingContext2D,
    vp: ChartViewport,
    series: KlineSeries,
    hover: HoverState,
    precision: number,
    symbol: string,
    interval: string,
  ): number {
    const n = series.length;
    if (n === 0) return vp.bodyTop + 4;

    const i = hover.active && hover.index >= 0 && hover.index < n ? hover.index : n - 1;
    const o = series.open.get(i);
    const h = series.high.get(i);
    const l = series.low.get(i);
    const c = series.close.get(i);
    const v = series.volume.get(i);
    const chg = c - o;
    const chgPct = o !== 0 ? (chg / o) * 100 : 0;
    const col = c >= o ? C.up : C.down;

    let x = vp.plotLeft + 8;
    const y = vp.bodyTop + 4;

    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    ctx.font = F.smBold;
    ctx.fillStyle = C.text0;
    ctx.fillText(symbol, x, y);
    x += ctx.measureText(symbol).width + 6;

    ctx.font = F.xs;
    ctx.fillStyle = C.text2;
    ctx.fillText(interval.toUpperCase(), x, y + 1);
    x += ctx.measureText(interval).width + 12;

    const pairs: [string, number][] = [
      ['O', o],
      ['H', h],
      ['L', l],
      ['C', c],
    ];
    for (const [k, val] of pairs) {
      ctx.fillStyle = C.text3;
      ctx.fillText(k, x, y + 1);
      x += ctx.measureText(k).width + 3;
      const s = fmtPrice(val, precision);
      ctx.fillStyle = col;
      ctx.fillText(s, x, y + 1);
      x += ctx.measureText(s).width + 9;
    }

    const chgTxt = `${chg >= 0 ? '+' : ''}${fmtPrice(chg, precision)} (${chgPct >= 0 ? '+' : ''}${chgPct.toFixed(2)}%)`;
    ctx.fillStyle = col;
    ctx.fillText(chgTxt, x, y + 1);
    x += ctx.measureText(chgTxt).width + 10;

    ctx.fillStyle = C.text3;
    ctx.fillText('V', x, y + 1);
    x += 9;
    ctx.fillStyle = C.text2;
    ctx.fillText(compact(v), x, y + 1);

    return y + 15;
  }

  /** Bottom-left status strip: bar count, loading state, autoscale mode. */
  drawFooter(ctx: CanvasRenderingContext2D, vp: ChartViewport, notes: string[]): void {
    if (notes.length === 0) return;
    ctx.font = F.micro;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'bottom';
    ctx.fillStyle = C.text3;
    let x = vp.plotLeft + 6;
    for (const note of notes) {
      ctx.fillText(note, x, vp.bodyBottom - 4);
      x += ctx.measureText(note).width + 12;
    }
  }

  /** Centred message for the empty / loading state. */
  drawMessage(ctx: CanvasRenderingContext2D, vp: ChartViewport, text: string): void {
    ctx.font = F.sm;
    ctx.fillStyle = C.text3;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, vp.plotLeft + vp.plotWidth / 2, vp.bodyTop + vp.bodyHeight / 2);
  }

  /** Session-boundary markers — a vertical rule at each UTC midnight. */
  drawSessions(ctx: CanvasRenderingContext2D, vp: ChartViewport, series: KlineSeries): void {
    if (series.length === 0 || series.intervalMs >= 86_400_000) return;
    const lo = Math.max(1, vp.firstVisible);
    const hi = Math.min(series.length - 1, vp.lastVisible);
    if (hi < lo) return;
    if (vp.barWidth < 1.5) return;

    const t = series.openTime.data;
    ctx.strokeStyle = C.gridStrong;
    ctx.lineWidth = 1;
    ctx.setLineDash([2, 4]);
    ctx.beginPath();
    for (let i = lo; i <= hi; i++) {
      const prev = new Date(t[i - 1]).getUTCDate();
      const cur = new Date(t[i]).getUTCDate();
      if (prev === cur) continue;
      const x = crisp(vp.xOf(i) - vp.barWidth / 2);
      ctx.moveTo(x, vp.bodyTop);
      ctx.lineTo(x, vp.bodyBottom);
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }

  /** Timestamp of the most recent print, drawn small under the legend. */
  drawClock(ctx: CanvasRenderingContext2D, vp: ChartViewport, ts: number): void {
    if (!ts) return;
    ctx.font = F.micro;
    ctx.fillStyle = C.text3;
    ctx.textAlign = 'right';
    ctx.textBaseline = 'top';
    ctx.fillText(hms(ts), vp.plotRight - 6, vp.bodyTop + 4);
  }
}
